<template>
  <div class="mortgage-progress-component">
    <a-tabs :tab-position="'top'" v-if="loaded && hasMortgageProgressSummary">
      <a-tab-pane key="year" tab="Jaaroverzicht">
        <table class="table">
          <thead>
          <tr>
            <th>Maand</th>
            <th class="align-right">Rente</th>
            <th class="align-right">Aftrekbaar</th>
            <th class="align-right">Inleggen/ Aflossing</th>
            <th class="align-right">Vermogen</th>
            <th class="align-right">Bruto</th>
            <th class="align-right">Fiscaal</th>
            <th class="align-right">Netto</th>
            <th class="align-right">Hypotheek</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="progressItem in paginatedYearlyMortgageProgress">
            <td>
              <span class="nowrap">{{ pad(progressItem.month, 2) + '-' + progressItem.year }}</span>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.interest_amount" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.interest_amount" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.deductible_monthly_obligations_amount" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.deductible_monthly_obligations_amount" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.repayment_amount" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.repayment_amount" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.accumulated_value" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.accumulated_value" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.gross_monthly_payment" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.gross_monthly_payment" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="(progressItem.gross_monthly_payment - progressItem.net_monthly_payment)"
                          decimal-places="2"/>
                </template>
                <amount v-bind:value="(progressItem.gross_monthly_payment - progressItem.net_monthly_payment)"
                        decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.net_monthly_payment" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.net_monthly_payment" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.remaining_mortgage_sum" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.remaining_mortgage_sum" decimal-places="0"/>
              </a-popover>
            </td>
          </tr>
          <tr>
            <th>Totaal</th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalInterestAmount" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalInterestAmount" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th></th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalRepaymentAmount" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalRepaymentAmount" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th></th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalGrossPayments" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalGrossPayments" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalFiscal" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalFiscal" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalNetPayments" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalNetPayments" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th></th>
          </tr>
          <tr v-if="showMoreYearly">
            <td colspan="9" class="align-center">
              <a-button type="link" v-on:click="onShowMoreYearly">Meer tonen</a-button>
            </td>
          </tr>
          </tbody>
        </table>
      </a-tab-pane>
      <a-tab-pane key="month" tab="Maandoverzicht">
        <table class="table">
          <thead>
          <tr>
            <th>Maand</th>
            <th class="align-right">Rente</th>
            <th class="align-right">Aftrekbaar</th>
            <th class="align-right">Inleggen/ Aflossing</th>
            <th class="align-right">Vermogen</th>
            <th class="align-right">Bruto</th>
            <th class="align-right">Fiscaal</th>
            <th class="align-right">Netto</th>
            <th class="align-right">Hypotheek</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="progressItem in monthlyMortgageProgress">
            <td>
              <span class="nowrap">{{ pad(progressItem.month, 2) + '-' + progressItem.year }}</span>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.interest_amount" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.interest_amount" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.deductible_monthly_obligations_amount" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.deductible_monthly_obligations_amount" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.repayment_amount" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.repayment_amount" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.accumulated_value" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.accumulated_value" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.gross_monthly_payment" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.gross_monthly_payment" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="(progressItem.gross_monthly_payment - progressItem.net_monthly_payment)"
                          decimal-places="2"/>
                </template>
                <amount v-bind:value="(progressItem.gross_monthly_payment - progressItem.net_monthly_payment)"
                        decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.net_monthly_payment" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.net_monthly_payment" decimal-places="0"/>
              </a-popover>
            </td>
            <td class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="progressItem.remaining_mortgage_sum" decimal-places="2"/>
                </template>
                <amount v-bind:value="progressItem.remaining_mortgage_sum" decimal-places="0"/>
              </a-popover>
            </td>
          </tr>
          <tr>
            <th>Totaal</th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalInterestAmount" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalInterestAmount" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th></th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalRepaymentAmount" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalRepaymentAmount" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th></th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalGrossPayments" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalGrossPayments" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalFiscal" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalFiscal" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th class="align-right">
              <a-popover placement="topLeft">
                <template slot="content">
                  <amount v-bind:value="totalNetPayments" decimal-places="2" class="nowrap"/>
                </template>
                <amount v-bind:value="totalNetPayments" decimal-places="0" class="nowrap"/>
              </a-popover>
            </th>
            <th></th>
          </tr>
          <tr v-if="showMoreMonthly">
            <td colspan="9" class="align-center">
              <a-button type="link" v-on:click="onShowMoreMonthly">Meer tonen</a-button>
            </td>
          </tr>
          </tbody>
        </table>
      </a-tab-pane>
    </a-tabs>
    <div v-if="loaded && !hasMortgageProgressSummary" style="margin: 1.5rem; display: inline-block;">
      Kan overzicht niet ophalen door onvoldoende gegevens.
    </div>
  </div>
</template>
<script>
import mortgageProgressSummaryApi from '~/api/mortgage_progress_summary'

export default {
  props: {
    mortgageId: {
      type: String,
      required: true,
    }
  },
  data () {
    return {
      loaded: false,
      mortgageProgressSummary: {},
      mortgageProgress: [],
      showYearlyItems: 5,
      showMonthlyItems: 5,
    }
  },
  computed: {
    hasMortgageProgressSummary () {
      return typeof this.mortgageProgressSummary.interest_amount !== 'undefined'
          && this.mortgageProgressSummary.interest_amount !== null
    },
    totalInterestAmount () {
      return this.mortgageProgressSummary.interest_amount
    },
    totalRepaymentAmount () {
      return this.mortgageProgressSummary.repayment_amount
    },
    totalGrossPayments () {
      return this.mortgageProgressSummary.gross_payments
    },
    totalFiscal () {
      if (Object.keys(this.mortgageProgressSummary).length > 0) {
        return this.mortgageProgressSummary.gross_payments - this.mortgageProgressSummary.net_payments
      }
    },
    totalNetPayments () {
      return this.mortgageProgressSummary.net_payments
    },
    selectedMortgageProgress () {
      return this.mortgageProgress.slice(0, 10)
    },
    yearlyMortgageProgress () {
      let groupedValues = {}

      for (let key in this.futureMortgageProgress) {
        let progressItem = this.futureMortgageProgress[key]

        if (groupedValues[progressItem.year]) {
          continue
        }

        groupedValues[progressItem.year] = progressItem
      }

      return Object.values(groupedValues)
    },
    paginatedYearlyMortgageProgress () {
      return this.yearlyMortgageProgress.slice(0, this.showYearlyItems)
    },
    monthlyMortgageProgress () {
      return this.futureMortgageProgress.slice(0, this.showMonthlyItems)
    },
    futureMortgageProgress () {
      return this.mortgageProgress.filter((progressItem) => {
        return progressItem.year > this.currentYear
            || (progressItem.year === this.currentYear && progressItem.month >= this.currentMonth)
      })
    },
    showMoreYearly () {
      return this.showYearlyItems < this.yearlyMortgageProgress.length
    },
    showMoreMonthly () {
      return this.showMonthlyItems < this.mortgageProgress.length
    },
    currentYear () {
      return parseInt((new Date()).getFullYear())
    },
    currentMonth () {
      return parseInt((new Date()).getMonth()) + 1
    }
  },
  mounted () {
    this.initMortgageProgressSummary()
  },
  methods: {
    initMortgageProgressSummary () {
      return mortgageProgressSummaryApi.getByMortgageId(this.mortgageId)
          .then(response => {
            this.mortgageProgressSummary = response.data.summary
            this.mortgageProgress = response.data.mortgage_progress
          })
          .catch(error => {
            if (error.response.status !== 422) {
              throw error;
            }
          })
          .finally(() => {
            this.loaded = true
          })
    },
    pad (value) {
      return value < 10 ? '0' + value : value
    },
    groupBy (xs, key) {
      return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x)
        return rv
      }, [])
    },
    onShowMoreYearly () {
      this.showYearlyItems += 5
    },
    onShowMoreMonthly () {
      this.showMonthlyItems += 5
    }
  }
}
</script>
<style scoped>
.align-right {
  text-align: right;
}

.align-center {
  text-align: center;
}

.nowrap {
  white-space: nowrap;
}

table tr th {
  background: none;
}
</style>